import { NutritionProgramme } from '../../services/nutritionService';
import { flattenOrchards } from './NutritionMembershipPicker';

export interface SetupRow {
  id: string;
  season: string;
  /** Vendor, or '' when absent. The PAGE renders the em dash — this is data. */
  source: string;
  status: NutritionProgramme['status'];
  rounds: number;
  orchards: number;
  programme: NutritionProgramme;
}

/**
 * Distinct orchards across every round of a programme.
 *
 * A UNION, not a sum: the same block is normally fed in several rounds, and a
 * sum would report three times the farm.
 *
 * Reuses flattenOrchards (NutritionMembershipPicker) for the per-round flatten
 * rather than growing a second one.
 */
export function countProgrammeOrchards(p: NutritionProgramme): number {
  const all: string[] = [];
  (p.rounds || []).forEach((r) => { flattenOrchards(r).forEach((c) => all.push(c)); });
  // Array.from, not [...new Set(...)]: CRA targets ES5 with no
  // downlevelIteration, so the spread form passes jest and breaks npm run build.
  return Array.from(new Set(all)).length;
}

export function buildSetupRows(programmes: NutritionProgramme[]): SetupRow[] {
  return (programmes || []).map((p) => ({
    id: p._id,
    season: p.season || '',
    source: p.source?.vendor || '',
    status: p.status,
    rounds: p.rounds?.length ?? 0,
    orchards: countProgrammeOrchards(p),
    programme: p,
  }));
}

export function filterSetupRows(
  rows: SetupRow[],
  search: string,
  status: 'all' | 'active' | 'received',
): SetupRow[] {
  const q = search.trim().toLowerCase();
  return rows.filter((r) => {
    if (status !== 'all' && r.status !== status) return false;
    if (!q) return true;
    return `${r.season} ${r.source}`.toLowerCase().includes(q);
  });
}

/**
 * Derives the next `r<N>` id from the programme's OWN rounds (max numeric
 * suffix + 1), mirroring `nextGroupId` (foliarRoundEditor.ts) exactly: a
 * stateless derivation instead of the old page's module-level `roundSeq`
 * counter, which needed a hydrate-time reseed to avoid colliding with
 * existing ids. A non-numeric suffix (or an id not starting with "r") is
 * ignored rather than propagated into `rNaN`.
 */
export function nextRoundId(programme: Pick<NutritionProgramme, 'rounds'>): string {
  const maxSuffix = (programme.rounds || []).reduce((max, r) => {
    const n = r.roundId.startsWith('r') ? Number(r.roundId.slice(1)) : NaN;
    return Number.isFinite(n) ? Math.max(max, n) : max;
  }, 0);
  return `r${maxSuffix + 1}`;
}

export interface YoungTreeSummary { present: boolean; name: string; productNames: string[]; }

/**
 * Read-only projection of the programme-level young-tree sub-program.
 *
 * `present` is derived from the sub-document EXISTING, not from its product
 * list — buildYoungTree persists a name-only sub-program deliberately.
 */
export function youngTreeSummary(p: NutritionProgramme | null): YoungTreeSummary {
  const yt = p?.youngTreeProgramme;
  if (!yt) return { present: false, name: '', productNames: [] };
  return {
    present: true,
    name: yt.name || '',
    productNames: (yt.products || []).map((x) => x.productName || x.productId),
  };
}
